import React, { createContext, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { FeatureFlagKey } from '@reward-platform/devcycle'
import { getActiveFeatureFlags } from '~/services/featureFlagService'
import { PlatformError } from '~/utils/errors'
import { useAuthContext } from '../auth'
import { evaluateFeatureFlagValue } from './FeatureFlag.utils'
import type { FeatureFlagContextValue, FeatureFlagProviderProps } from './FeatureFlag.types'

// Documentation around DevCycle usage here: https://aviosgroup.atlassian.net/wiki/spaces/RP/pages/2564981363/Feature+Flags+with+DevCycle

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined)

export const FeatureFlagProvider = ({
  children,
  initialFeatureFlags,
}: React.PropsWithChildren<FeatureFlagProviderProps>) => {
  const { user } = useAuthContext()
  const { data, isLoading, isError } = useQuery({
    queryKey: user ? [user.sub, user.email] : ['unauthenticated_user'],
    queryFn: getActiveFeatureFlags,
    initialData: initialFeatureFlags,
    meta: {
      errorMessage: 'Failed to fetch feature toggles',
    },
  })

  const activeFeatureFlags = useMemo(() => (data && !isError ? { ...data } : {}), [data, isError])

  const context: FeatureFlagContextValue = useMemo(
    () => ({ activeFeatureFlags, isLoading }),
    [activeFeatureFlags, isLoading]
  )

  return <FeatureFlagContext.Provider value={context}>{children}</FeatureFlagContext.Provider>
}

export const useFeatureFlagContext = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new PlatformError('useFeatureFlagContext must be used within an FeatureFlagProvider')
  }
  return context ?? {}
}

export const useFeatureFlag = (key: FeatureFlagKey, defaultValue = false): boolean => {
  const { activeFeatureFlags } = useFeatureFlagContext()
  return !!evaluateFeatureFlagValue<boolean>(activeFeatureFlags, key, defaultValue)
}

export default FeatureFlagContext
